import { useApolloClient, useQuery } from "@apollo/client";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { size } from "lodash";
import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import chatApi from "../../../../api/chatApi";
import { GetCurrentUserInfoDocument } from "../../../../generated";
import { useBrightbotStore } from "../../../../store/useBrightbotStore";
import {
  useSessionActions,
  useSessionStore,
} from "../../../../store/useSessionStore";
import { refreshTokenIfNeeded } from "../../../../utils/tokenManagement";
import {
  InterruptType,
  streamErrorTypes,
  streamEventType,
  userType,
} from "../../../types";
import useInterrupt from "./useInterrupt";

let controller: any;

const initialStreamError = { error: "", message: "" };

const useChatStream = () => {
  const navigate = useNavigate();
  const { workspaceId, threadId } = useParams();
  const { handleInterrupt } = useInterrupt();
  const apolloClient = useApolloClient();

  const messages = useSessionStore.use.messages();
  const thread = useSessionStore.use.thread();
  const refetch = useSessionStore.use.refetch();

  const reeasoningEnabled = useSessionStore.use.reasoningEnabled();
  const {
    setActiveRunId,
    setMessageValue,
    setStream,
    updateOutput,
    setMessages,
    storeMessageAsStream,
    setStreamMessage,
    setSidebar,
    setRefetch,
    setThread,
    setInterruptAction,
    appendNewStep,
    clearSteps,
  } = useSessionActions();

  const activeAssistant = useBrightbotStore.use.activeAssistant();

  const { data } = useQuery(GetCurrentUserInfoDocument);

  const userName = `${data?.currentUser?.firstName.split(" ").join("-") || ""}`;
  const userId = data?.currentUser.id;

  useEffect(() => {
    return () => {
      controller?.abort();
    };
  }, []);

  const streamContent = useCallback(
    async (newMessage: any) => {
      storeMessageAsStream(newMessage);
    },
    [storeMessageAsStream]
  );

  const newMessagesStreamerHandler = useCallback(
    async (messages: any) => {
      for (const message of messages) {
        if (message.type === userType.ai) {
          await streamContent(message);
          await new Promise((resolve) => setTimeout(resolve, 5));
        }
      }
    },
    [streamContent]
  );

  const streamCallingHandler = async (
    id: string | undefined,
    requestBody: any
  ) => {
    controller = new AbortController();
    const signal = controller?.signal;
    let streamError = initialStreamError;
    let token = localStorage.getItem("token") || "";

    if (!token || !workspaceId) {
      toast.error(
        "Issue running the stream, Missing Details, please try again later"
      );
      return;
    }

    // Check token TTL and refresh if needed before making the stream call
    try {
      token = await refreshTokenIfNeeded(token, apolloClient);
    } catch (error) {
      console.error("Error checking/refreshing token:", error);
      // Continue with original token
    }

    // Add session_info to the request body with the refreshed token
    const enhancedRequestBody = {
      ...requestBody,
      input: {
        ...requestBody.input,
        session_info: JSON.stringify({
          workspace_id: workspaceId,
          token: token,
        }),
      },
    };

    await fetchEventSource(
      `${process.env.REACT_APP_BASE_URL}/threads/${id}/runs/stream`,
      {
        signal: signal,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": process.env.REACT_APP_BRIGHTIBOT_API_KEY || "",
        },
        body: JSON.stringify(enhancedRequestBody),
        openWhenHidden: true,
        async onopen(res) {
          if (res.status === 200) {
            setStream(true);
            // Reset any previous outputs/reasoning when starting a new stream
            updateOutput([]);
          }
        },
        onmessage(msg: any) {
          let dt;
          try {
            if (msg?.data) {
              dt = JSON.parse(msg.data);
            } else {
              return; // Skip processing for messages without data (like heartbeats)
            }
          } catch (error) {
            return;
          }

          // Handle different event types
          if (msg.event === streamEventType.metadata) {
            setActiveRunId(dt?.run_id);
          } else if (
            msg.event === streamEventType.updates &&
            dt?.__interrupt__
          ) {
            const value = dt?.__interrupt__[0]?.value as InterruptType;
            handleInterrupt(value);
            controller.abort();
            return;
          } else if (msg.event === streamEventType.error) {
            streamError = { error: dt?.error, message: dt?.message };
          } else if (msg.event && msg.event === streamEventType.custom) {
            appendNewStep(dt[0].steps[0]);
          }
        },
        onclose() {
          // clear steps then display final message
          clearSteps();
          setInterruptAction(null);
          setStream(false);
          setActiveRunId(undefined);

          chatApi
            .get(`/threads/${id}/get_state`)
            .then((res) => {
              const { outputs, messages } = res?.data?.values;

              // Check if this is still the current thread
              const currentThreadId = threadId || id;
              if (currentThreadId !== id) {
                return; // Don't update UI with data from a different thread
              }

              // update outputs, plan and reasoning when request is complete
              updateOutput(outputs || []);

              newMessagesStreamerHandler(messages.splice(-1));

              if (streamError.error === streamErrorTypes.UserInterrupt) {
                streamError = initialStreamError;
                return;
              }

              setSidebar(true);
            })
            .catch((error) => {
              // Keep this error log for production troubleshooting
              console.error("Failed to fetch thread state:", error);
            });
        },
        onerror(err) {
          throw new Error(err);
        },
      }
    );
  };

  const streamHITLHandler = async (id: string | undefined, data: any) => {
    const requestBody = {
      assistant_id: activeAssistant?.assistant_id,
      command: {
        resume: data,
      },
      input: {
        // session_info will be added in streamCallingHandler
      },
      config: {
        configurable: {
          thread_id: id, // Your thread/conversation ID
        },
      },
      user_info: userId,
      stream_mode: ["custom", "updates"],
      stream_subgraphs: true,
    };

    await streamCallingHandler(id, requestBody);
  };

  const streamHandler = async (
    id: string | undefined,
    data: any,
    message: string
  ) => {
    const requestBody = {
      assistant_id: activeAssistant?.assistant_id,
      ...(messages.length === 0 && {
        metadata: { Title: message, Author: userName },
      }),
      input: {
        // session_info will be added in streamCallingHandler
        reasoning_enabled: reeasoningEnabled,
        messages: [data[size(data) - 1]], // send only the latest message
      },
      user_info: userId,
      stream_mode: ["custom", "updates"],
      stream_subgraphs: true,
    };

    await streamCallingHandler(id, requestBody);
  };

  const chatCreate = async (data: any, message: string) => {
    let id = threadId;
    // Clear any existing outputs when starting a new chat
    if (!thread) {
      // For a new thread, reset any previous outputs/reasoning
      updateOutput([]);
      setStreamMessage("");
      // Create a new thread
      id = await createSession(message);
    }

    await streamHandler(id, data, message);
  };

  const manualSubmit = async (messageValue: string) => {
    try {
      const data = messageValue;

      if (!data) return;

      const dataMessage = [
        ...messages,
        {
          content: data,
          additional_kwargs: {},
          response_metadata: {},
          type: userType.human,
          name: userName,
          example: false,
        },
      ];
      setMessageValue("");
      setMessages(dataMessage);

      await chatCreate(dataMessage, data);
    } catch (err) {
      console.log(err);
    }
  };

  const onSubmit = async (e: any) => {
    try {
      e.preventDefault();

      if (!e.currentTarget.message.value) return;

      const data = e.currentTarget.message.value;

      if (!data) return;

      const dataMessage = [
        ...messages,
        {
          content: data,
          additional_kwargs: {},
          response_metadata: {},
          type: userType.human,
          name: userName,
          example: false,
        },
      ];
      setMessageValue("");
      setMessages(dataMessage);
      e?.currentTarget?.reset();

      await chatCreate(dataMessage, data);
    } catch (err) {
      console.log(err);
    }
  };

  const createSession = async (message: string) => {
    try {
      const res = await chatApi.post("/threads", {
        metadata: {
          user_id: userId,
          workspace_id: workspaceId,
          Title: message,
          Author: userName,
        },
      });

      const id = res.data.thread_id;
      setThread(id);
      if (setRefetch) {
        setRefetch(!refetch);
        navigate(`/workspace/${workspaceId}/brightagent/${id}`);
      }
      return id;
    } catch (e) {
      console.log(e);
    }
  };

  return {
    onSubmit,
    manualSubmit,
    streamHITLHandler,
  };
};

export default useChatStream;
